<script lang="ts">
  // Shared lock-up for the public landing header and the authenticated coach
  // header. By default it is the [GetZero logo] Green Skills Coach lock-up; a
  // branded tenant swaps in its own logo and title.
  //
  // Whether this renders as one combined in-app link or two separate links
  // depends only on whether a tenant logo was *configured* (`logoUrl`), not on
  // whether that logo is currently loading successfully:
  //   - No tenant logo configured at all: the true default, unbranded surface.
  //     The GetZero mark is its own link to getzero.earth (a new tab, reusing
  //     the pattern from PoweredByGetZero); the title next to it is a separate
  //     in-app "home" link.
  //   - A tenant logo is configured, whether it's currently loading or has
  //     failed and fallen back to the GetZero mark image: this is still a
  //     tenant-branded surface, so the mark and title stay a single combined
  //     in-app link, exactly as before this component grew an external link at
  //     all. A failed tenant logo must never start pointing at getzero.earth -
  //     that would be GetZero's own link appearing on someone else's branded
  //     page - and a single link also keeps the tenant logo image accessible:
  //     splitting it out with alt="" would leave it an unnamed link on its own.
  export let href: string;
  export let logoUrl: string | null = null;
  export let title = "Green Skills Coach";

  const GETZERO_URL = "https://www.getzero.earth";

  // A dead or forbidden logo URL must not leave a broken-image icon in the
  // header, so the default mark image takes over. Tracked per URL so a later
  // tenant's logo still gets its own chance to load.
  let failedLogoUrl = "";
  $: hasTenantLogo = Boolean(logoUrl);
  $: customLogo = logoUrl && logoUrl !== failedLogoUrl ? logoUrl : null;
</script>

{#if hasTenantLogo}
  <a {href} class="brand-lockup brand-lockup-link flex min-h-[44px] min-w-0 items-center gap-2 rounded-lg sm:gap-3">
    <!-- The adjacent title carries the organisation name, so repeating it here
         would have a screen reader announce the same words twice - true of the
         tenant's own logo and of the GetZero mark it falls back to. -->
    {#if customLogo}
      <img
        src={customLogo}
        alt=""
        class="tenant-logo w-auto shrink-0 object-contain"
        on:error={() => (failedLogoUrl = customLogo || "")} />
    {:else}
      <img src="/getzero-mark-reverse.svg" alt="" width="34" height="40" class="h-9 w-auto shrink-0 sm:h-11" />
    {/if}
    <!-- UAT: the mobile title read small next to the mark, so it steps up one Tailwind size
         (text-lg -> text-xl, 18px -> 20px). It stays truncated inside a min-w-0 flex item, so the
         extra 2px shortens the ellipsis budget at 320px rather than pushing the nav controls. -->
    <span
      class="brand-title min-w-0 truncate text-xl font-bold leading-none tracking-tight sm:text-3xl sm:tracking-normal"
      >{title}</span>
  </a>
{:else}
  <div class="brand-lockup flex min-h-[44px] min-w-0 items-center gap-2 sm:gap-3">
    <a
      href={GETZERO_URL}
      target="_blank"
      rel="noopener noreferrer"
      class="brand-lockup-link flex min-h-[44px] shrink-0 items-center rounded-lg"
      aria-label="GetZero (opens in a new tab)">
      <img src="/getzero-mark-reverse.svg" alt="" width="34" height="40" class="h-9 w-auto shrink-0 sm:h-11" />
    </a>
    <a {href} class="brand-lockup-link flex min-h-[44px] min-w-0 items-center rounded-lg">
      <span
        class="brand-title min-w-0 truncate text-xl font-bold leading-none tracking-tight sm:text-3xl sm:tracking-normal"
        >{title}</span>
    </a>
  </div>
{/if}

<style>
  .brand-lockup-link:focus-visible {
    outline: 2px solid var(--brand-focus, #ffffff);
    outline-offset: 2px;
  }
  /* Falls back to white for the unbranded public landing header, which has no
     theme variables in scope. */
  .brand-title {
    color: var(--brand-on-header, #ffffff);
  }
  /* The envelope is fixed by CSS, so no uploaded dimension can size the header.
     object-fit: contain keeps the aspect ratio - a wide logo is never cropped
     or stretched to fill the box. */
  .tenant-logo {
    max-width: 132px;
    max-height: 56px;
  }
  @media (min-width: 640px) {
    .tenant-logo {
      max-width: 200px;
      max-height: 64px;
    }
  }
</style>
